import { useEffect, useRef, useState } from "react";

/**
 * Lightweight scroll-reveal wrapper. Adds a fade + lift when the element
 * enters the viewport. Honors prefers-reduced-motion. No deps.
 *
 * Props:
 *  - delay: ms delay before transition starts (use to stagger siblings)
 *  - y: initial Y offset in px (default 18)
 *  - once: only animate the first time it enters view (default true)
 */
export default function Reveal({
    children,
    className = "",
    delay = 0,
    y = 18,
    once = true,
    as: Tag = "div",
    ...rest
}) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (reduce) {
            setVisible(true);
            return;
        }
        const node = ref.current;
        if (!node) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setVisible(true);
                        if (once) obs.unobserve(entry.target);
                    } else if (!once) {
                        setVisible(false);
                    }
                });
            },
            { rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
        );
        obs.observe(node);
        return () => obs.disconnect();
    }, [once]);

    const style = {
        transition: "opacity 700ms cubic-bezier(0.22, 1, 0.36, 1), transform 700ms cubic-bezier(0.22, 1, 0.36, 1)",
        transitionDelay: `${delay}ms`,
        opacity: visible ? 1 : 0,
        transform: visible ? "translate3d(0, 0, 0)" : `translate3d(0, ${y}px, 0)`,
        willChange: "opacity, transform",
    };

    return (
        <Tag ref={ref} className={className} style={style} {...rest}>
            {children}
        </Tag>
    );
}
